/**
 * Harbor Therapy — pre-session intake questions.
 *
 * Returns ~20 questions tailored to a specific therapist based on
 * their specializations. The bulk (14 questions) are shared core;
 * the rest are pulled from specialization-specific modules.
 *
 * IMPORTANT: This is the pre-session questionnaire, NOT the full
 * clinical intake. Full clinical intake (medications, full history,
 * emergency contact, signed consents) happens through the secure
 * HIPAA-compliant client portal AFTER booking is confirmed.
 *
 * Question types:
 *   - text:     short single-line input
 *   - textarea: multi-line input
 *   - select:   dropdown
 *   - radio:    radio cards (mutually exclusive)
 *   - checkbox: checkbox grid (multiple selectable)
 *   - scale:    1-10 scale slider
 */

const CORE_QUESTIONS = [
    {
        id: "concerns_summary",
        type: "textarea",
        label: "In a few sentences, what's bringing you to therapy right now?",
        placeholder: "It's fine to be brief. We'll go deeper in session.",
        required: true,
    },
    {
        id: "concerns_duration",
        type: "select",
        label: "How long has this been going on?",
        options: [
            "Just recently (under a month)",
            "A few months",
            "About a year",
            "Several years",
            "As long as I can remember",
            "Hard to say",
        ],
        required: true,
    },
    {
        id: "current_stress_level",
        type: "scale",
        label: "On a scale of 1 to 10, how would you rate your current stress level?",
        min: 1,
        max: 10,
        minLabel: "Manageable",
        maxLabel: "Overwhelming",
        required: true,
    },
    {
        id: "previous_therapy",
        type: "radio",
        label: "Have you been in therapy before?",
        options: [
            { value: "no", label: "No, this is my first time" },
            { value: "yes-helpful", label: "Yes, and it was helpful" },
            { value: "yes-mixed", label: "Yes, with mixed results" },
            { value: "yes-not-helpful", label: "Yes, but it wasn't a fit" },
        ],
        required: true,
    },
    {
        id: "previous_therapy_detail",
        type: "textarea",
        label: "If you've been in therapy before, anything you want me to know about that experience?",
        placeholder: "What worked, what didn't, what you're hoping is different.",
        required: false,
        showIf: (answers) =>
            answers.previous_therapy && answers.previous_therapy.startsWith("yes"),
    },
    {
        id: "therapy_goals",
        type: "textarea",
        label: "What would feel like a meaningful change for you?",
        placeholder: "Even a vague answer is fine. We'll refine it together.",
        required: true,
    },
    {
        id: "support_system",
        type: "radio",
        label: "How would you describe your current support system?",
        options: [
            { value: "strong", label: "Strong — close people I can really talk to" },
            { value: "okay", label: "Okay — some people, but it varies" },
            { value: "limited", label: "Limited — I mostly handle things alone" },
            { value: "none", label: "Pretty isolated right now" },
        ],
        required: true,
    },
    {
        id: "sleep_quality",
        type: "radio",
        label: "How has your sleep been over the past month?",
        options: [
            { value: "good", label: "Generally good" },
            { value: "okay", label: "Up and down" },
            { value: "poor", label: "Consistently poor" },
            { value: "very-poor", label: "Very disrupted — affecting daily life" },
        ],
        required: true,
    },
    {
        id: "physical_health",
        type: "radio",
        label: "How would you describe your overall physical health?",
        options: [
            { value: "good", label: "Good — no major concerns" },
            { value: "managing", label: "Managing some chronic stuff" },
            { value: "struggling", label: "Struggling — it's affecting my mental health" },
            { value: "rather-not", label: "Rather not say right now" },
        ],
        required: true,
    },
    {
        id: "substances",
        type: "radio",
        label: "How would you describe your relationship with alcohol or substances?",
        options: [
            { value: "none", label: "I don't use them" },
            { value: "occasional", label: "Occasional, no concerns" },
            { value: "questioning", label: "Sometimes I wonder if it's too much" },
            { value: "concerning", label: "It's something I'd like to look at" },
            { value: "rather-not", label: "Rather not say right now" },
        ],
        required: true,
    },
    {
        id: "session_frequency_pref",
        type: "radio",
        label: "How often would you ideally like to meet?",
        options: [
            { value: "weekly", label: "Weekly" },
            { value: "biweekly", label: "Every other week" },
            { value: "flexible", label: "Flexible — depends what comes up" },
            { value: "not-sure", label: "Not sure yet" },
        ],
        required: true,
    },
    {
        id: "what_helps",
        type: "textarea",
        label: "When you're having a hard time, what tends to help — even a little?",
        placeholder: "Anything from walks to specific people to particular routines.",
        required: false,
    },
    {
        id: "approach_preference",
        type: "radio",
        label: "What kind of therapeutic relationship works best for you?",
        options: [
            { value: "direct", label: "Direct — challenge me, be honest" },
            { value: "warm", label: "Warm and supportive — let me lead the pace" },
            { value: "structured", label: "Structured — clear goals and frameworks" },
            { value: "exploratory", label: "Exploratory — open-ended and reflective" },
            { value: "not-sure", label: "Not sure yet — let's figure it out" },
        ],
        required: true,
    },
    {
        id: "anything_else",
        type: "textarea",
        label: "Anything else you'd like me to know before our first session?",
        placeholder: "Optional. Whatever feels useful.",
        required: false,
    },
];

// Specialization-specific question banks
const SPEC_QUESTIONS = {
    "Anxiety": [
        {
            id: "anxiety_frequency",
            type: "radio",
            label: "How often does anxiety significantly affect your daily life?",
            options: [
                { value: "rarely", label: "Rarely" },
                { value: "weekly", label: "A few times a week" },
                { value: "daily", label: "Daily" },
                { value: "constant", label: "Almost constantly" },
            ],
            required: true,
        },
        {
            id: "anxiety_manifests",
            type: "checkbox",
            label: "How does your anxiety usually show up? (Select all that apply)",
            options: [
                "Racing thoughts or worry loops",
                "Physical symptoms (chest, stomach, breathing)",
                "Panic attacks",
                "Avoidance of certain situations",
                "Sleep disruption",
                "Irritability or short fuse",
                "Difficulty making decisions",
            ],
            required: false,
        },
    ],
    "Trauma & PTSD": [
        {
            id: "trauma_processing_readiness",
            type: "radio",
            label: "How do you feel about exploring difficult past experiences in therapy?",
            options: [
                { value: "ready", label: "Ready — that's part of why I'm here" },
                { value: "open", label: "Open to it, with the right pacing" },
                { value: "cautious", label: "Cautious — I want to feel safe first" },
                { value: "not-yet", label: "Not yet — I'd like to focus on coping first" },
            ],
            required: true,
        },
        {
            id: "grounding_familiarity",
            type: "radio",
            label: "How familiar are you with grounding or nervous-system regulation techniques?",
            options: [
                { value: "very", label: "Very — I use them regularly" },
                { value: "some", label: "I know a few" },
                { value: "little", label: "I've heard the terms but don't really use them" },
                { value: "none", label: "Not at all" },
            ],
            required: false,
        },
    ],
    "Depression": [
        {
            id: "depression_intensity",
            type: "scale",
            label: "Over the past two weeks, how would you rate your overall mood?",
            min: 1,
            max: 10,
            minLabel: "Very low",
            maxLabel: "Generally good",
            required: true,
        },
        {
            id: "depression_motivation",
            type: "radio",
            label: "How is your motivation and energy for daily activities?",
            options: [
                { value: "ok", label: "Mostly okay" },
                { value: "diminished", label: "Noticeably lower than usual" },
                { value: "very-low", label: "Very low — basics feel hard" },
                { value: "varies", label: "Varies a lot day to day" },
            ],
            required: true,
        },
    ],
    "Couples & relationships": [
        {
            id: "relationship_status",
            type: "radio",
            label: "If applicable, what's your relationship status?",
            options: [
                { value: "partnered-stable", label: "Partnered, generally stable" },
                { value: "partnered-struggling", label: "Partnered, struggling" },
                { value: "partnered-considering", label: "Partnered, considering separation" },
                { value: "single-recent", label: "Recently single" },
                { value: "single-long", label: "Single for a while" },
                { value: "complicated", label: "It's complicated" },
                { value: "rather-not", label: "Rather not say" },
            ],
            required: false,
        },
        {
            id: "relationship_focus",
            type: "textarea",
            label: "If relationships are part of why you're here, what specifically is on your mind?",
            placeholder: "Patterns you've noticed, a specific situation, or general questions.",
            required: false,
        },
    ],
    "Burnout & work stress": [
        {
            id: "work_satisfaction",
            type: "scale",
            label: "How would you rate your current work satisfaction?",
            min: 1,
            max: 10,
            minLabel: "Dread it",
            maxLabel: "Genuinely engaged",
            required: true,
        },
        {
            id: "work_boundaries",
            type: "radio",
            label: "How would you describe your work boundaries?",
            options: [
                { value: "strong", label: "Strong — work stays at work" },
                { value: "porous", label: "Porous — I'm often thinking about it" },
                { value: "nonexistent", label: "Basically nonexistent" },
                { value: "rebuilding", label: "Trying to rebuild them" },
            ],
            required: true,
        },
    ],
    "Identity & LGBTQ+": [
        {
            id: "identity_focus",
            type: "checkbox",
            label: "Are any of these part of what's on your mind? (Optional, select all that apply)",
            options: [
                "Sexual orientation",
                "Gender identity",
                "Coming out (to self, family, work, etc.)",
                "Navigating relationships and dating",
                "Family of origin acceptance",
                "Religious or cultural deconstruction",
                "Just exploring — no specific issue",
            ],
            required: false,
        },
        {
            id: "identity_safety",
            type: "radio",
            label: "How safe and supported do you feel in your current environment around your identity?",
            options: [
                { value: "very-safe", label: "Very safe and supported" },
                { value: "mostly", label: "Mostly safe, some areas of concern" },
                { value: "mixed", label: "Mixed — depends on the context" },
                { value: "unsafe", label: "Limited safety in important parts of my life" },
            ],
            required: false,
        },
    ],
    "Eating concerns": [
        {
            id: "eating_concerns_history",
            type: "radio",
            label: "Do you have a current or past relationship with disordered eating?",
            options: [
                { value: "current-active", label: "Yes, actively struggling" },
                { value: "current-recovering", label: "Yes, in active recovery" },
                { value: "past", label: "In the past, working to maintain recovery" },
                { value: "questioning", label: "Wondering if this applies to me" },
                { value: "no", label: "No — but body image is part of why I'm here" },
            ],
            required: true,
        },
        {
            id: "eating_treatment_team",
            type: "radio",
            label: "Are you currently working with other providers around this?",
            options: [
                { value: "full-team", label: "Yes — dietitian, physician, etc." },
                { value: "some", label: "Some — but not a full team" },
                { value: "none", label: "Not currently" },
                { value: "looking", label: "Looking for referrals" },
            ],
            required: false,
        },
    ],
    "ADHD & executive function": [
        {
            id: "adhd_diagnosis_status",
            type: "radio",
            label: "What's your current relationship to an ADHD diagnosis?",
            options: [
                { value: "diagnosed", label: "Formally diagnosed" },
                { value: "self-recognized", label: "Self-recognized, not formally diagnosed" },
                { value: "questioning", label: "Wondering if I have it" },
                { value: "diagnosed-treated", label: "Diagnosed and currently in treatment (medication, etc.)" },
            ],
            required: true,
        },
        {
            id: "adhd_areas",
            type: "checkbox",
            label: "Which areas are most affected? (Select all that apply)",
            options: [
                "Work or school performance",
                "Time management and deadlines",
                "Relationships and communication",
                "Finances and admin tasks",
                "Self-care and routines",
                "Emotional regulation",
                "Sleep and rest",
            ],
            required: false,
        },
    ],
    "Perinatal & parenting": [
        {
            id: "perinatal_stage",
            type: "radio",
            label: "Where are you in the perinatal journey?",
            options: [
                { value: "trying", label: "Trying to conceive" },
                { value: "pregnant", label: "Pregnant" },
                { value: "postpartum-recent", label: "Postpartum (under 6 months)" },
                { value: "postpartum-extended", label: "Postpartum (6 months - 2 years)" },
                { value: "parenting-young", label: "Parenting young children" },
                { value: "loss", label: "Navigating pregnancy or infant loss" },
                { value: "other", label: "Other / prefer not to say" },
            ],
            required: false,
        },
        {
            id: "perinatal_specific_concerns",
            type: "checkbox",
            label: "Any of these resonate? (Optional)",
            options: [
                "Persistent low mood since giving birth",
                "Anxiety or intrusive thoughts about baby",
                "Feeling disconnected from baby or partner",
                "Identity shift / loss of self",
                "Difficulty with breastfeeding decisions",
                "Sleep deprivation impact",
                "Returning to work concerns",
            ],
            required: false,
        },
    ],
    "Grief & loss": [
        {
            id: "grief_recency",
            type: "radio",
            label: "How recently did the loss occur?",
            options: [
                { value: "very-recent", label: "Within the past month" },
                { value: "recent", label: "Within the past year" },
                { value: "older", label: "Over a year ago" },
                { value: "anniversary", label: "Older, but the anniversary is coming up" },
                { value: "ongoing", label: "Anticipatory or ongoing loss" },
            ],
            required: false,
        },
    ],
    "Life transitions": [
        {
            id: "transition_type",
            type: "checkbox",
            label: "What kind of transition are you in? (Select all that apply)",
            options: [
                "Career change or job loss",
                "Relationship beginning, ending, or shifting",
                "Becoming a parent",
                "Empty nest / kids leaving",
                "Move or relocation",
                "Loss of a loved one",
                "Health or aging changes",
                "Identity or values shift",
            ],
            required: false,
        },
    ],
    "Cultural & bicultural identity": [
        {
            id: "cultural_context",
            type: "textarea",
            label: "If cultural or bicultural identity is part of what's on your mind, can you say a bit about it?",
            placeholder: "First-gen experience, family expectations, navigating multiple cultures — whatever's relevant.",
            required: false,
        },
    ],
};

/**
 * Build the intake questionnaire for a specific therapist.
 *
 * Strategy:
 *   - Always include all 14 core questions
 *   - Add 1-2 questions from each of the therapist's primary specializations
 *   - Cap total around 20 questions to avoid fatigue
 */
export function buildIntakeForTherapist(therapist) {
    const specQuestions = [];
    const seen = new Set();

    for (const spec of therapist.specializations) {
        const bank = SPEC_QUESTIONS[spec];
        if (!bank) continue;

        for (const q of bank) {
            if (seen.has(q.id)) continue;
            specQuestions.push(q);
            seen.add(q.id);
            if (specQuestions.length >= 6) break;
        }
        if (specQuestions.length >= 6) break;
    }

    return [...CORE_QUESTIONS, ...specQuestions];
}

/**
 * Group questions into pages for the multi-step UI.
 * Roughly 5 questions per page.
 */
export function paginateQuestions(questions, perPage = 5) {
    const pages = [];
    for (let i = 0; i < questions.length; i += perPage) {
        pages.push(questions.slice(i, i + perPage));
    }
    return pages;
}